import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable from '../../../components/ui/DataTable';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import PeriodNotice from './PeriodNotice';
import { usePeriodBandsApi } from '../config/participants';

const BANDS = ['high', 'mid', 'low'];

// Analitik Plan 2 (spec §4.4, §9/3) — dönemin bant kopyası. Aktivasyonda ölçekten kopyalanır;
// İK gerekçeyle değiştirebilir (kapanmış dönemde de). Yalnız hedef sistemli dönemde seviye yoktur,
// bant hedef puanına eşiklerle uygulanır.
export default function PeriodBandsTab({ period }) {
  const { t } = useTranslation();
  const { fetching, bands, save } = usePeriodBandsApi(period?.id);
  const [open, setOpen] = useState(false);
  const levels = useMemo(() => bands?.levels || [], [bands]);
  const bandLabel = useCallback((b) => t(`performance.period.bands.${b}`), [t]);
  const bandOptions = useMemo(() => BANDS.map((id) => ({ id, name: bandLabel(id) })), [bandLabel]);

  const columns = useMemo(() => [
    { name: 'label', label: t('performance.period.bands.level'), options: { filter: false, sort: false } },
    { name: 'numericValue', label: t('performance.period.bands.numericValue'), options: { filter: false, sort: false } },
    { name: 'band', label: t('performance.period.bands.band'), options: { filter: false, sort: false, customBodyRender: (v) => bandLabel(v) } },
  ], [t, bandLabel]);

  const schema = useMemo(() => [
    ...levels.map((l) => ({
      name: `band_${l.levelId}`, label: l.label, type: 'select', grid: 6, required: true,
      options: bandOptions, idKey: 'id', labelKey: 'name',
    })),
    // Hedef puanı eşikleri yalnız hedef sistemi (A) açık dönemde anlamlıdır.
    ...(bands?.systemAEnabled ? [
      { name: 'bandHighFrom', label: t('performance.period.bands.highFrom'), type: 'number', grid: 6 },
      { name: 'bandLowBelow', label: t('performance.period.bands.lowBelow'), type: 'number', grid: 6 },
    ] : []),
    { name: 'reason', label: t('performance.period.bands.reason'), type: 'textarea', grid: 12, rows: 3, required: true, maxLength: 500 },
  ], [levels, bandOptions, bands, t]);

  const initialValues = useMemo(() => ({
    ...Object.fromEntries(levels.map((l) => [`band_${l.levelId}`, l.band])),
    bandHighFrom: bands?.bandHighFrom ?? null,
    bandLowBelow: bands?.bandLowBelow ?? null,
    reason: '',
  }), [levels, bands]);

  const handleSave = useCallback(async (values) => save({
    levels: levels.map((l) => ({ levelId: l.levelId, band: values[`band_${l.levelId}`] })),
    bandHighFrom: values.bandHighFrom === '' || values.bandHighFrom == null ? null : Number(values.bandHighFrom),
    bandLowBelow: values.bandLowBelow === '' || values.bandLowBelow == null ? null : Number(values.bandLowBelow),
    reason: values.reason,
  }), [save, levels]);

  return (
    <>
      {bands?.bandsChangedAt && <PeriodNotice>{t('performance.period.bands.changedNotice')}</PeriodNotice>}
      {bands && levels.length === 0 && <PeriodNotice>{t('performance.period.bands.noLevels')}</PeriodNotice>}
      <DataTable
        title={t('performance.period.bands.title')}
        fetching={fetching}
        columns={columns}
        data={levels}
        totalRowCount={levels.length}
        pageIndex={0}
        pageSize={Math.max(levels.length, 10)}
        canCreateNewRecord={Boolean(bands)}
        newButtonLabel={t('performance.period.bands.edit')}
        onNewButtonClick={() => setOpen(true)}
      />
      <GenericFormDrawer
        open={open}
        onClose={() => setOpen(false)}
        title={t('performance.period.bands.edit')}
        schema={schema}
        initialValues={initialValues}
        onSave={handleSave}
      />
    </>
  );
}
